// src/lib/Icon.jsx — Direction Artistique (DESIGN.md §5).
//
// Jeu d'icônes trait fin (lucide-react, déjà installé) qui remplace les emojis.
// DESIGN.md écrit les noms façon Tabler (`ti-*`) ; on les mappe 1:1 sur Lucide
// (même grammaire « outline », graisse uniforme).
//
// Usage : <Icon name="ti-calendar" size={18} /> — couleur héritée (currentColor)
// par défaut → marine en nominal, corail en actif/survol via la couleur du parent.
//
// Le map grandit au fil du fan-out (chaque lot ajoute les icônes de ses écrans).
// Si un name n'est pas mappé → fallback discret (rien de cassé), à compléter.

import {
  Calendar, CalendarDays, LayoutDashboard, MessageSquare, Briefcase, Building2,
  ListChecks, Target, Settings, Home, FileText, ClipboardList, BarChart3,
  Coins, TrendingUp, AlertTriangle, Package, Users, Sparkles, Star,
  Wifi, MapPin, Key, Clock, DoorOpen, Eye, ListChecks as ListDetails, Book,
  CheckSquare, Files, Monitor, Lock, Info, Flame, Pin, RefreshCw, CircleAlert,
  Euro, Globe, Link2, Settings2, User,
} from "lucide-react"

// Map ti-* (DESIGN) → composant Lucide. Couvre la nav partagée + les sens communs.
const TI = {
  "ti-calendar":        Calendar,
  "ti-calendar-month":  CalendarDays,
  "ti-layout-dashboard":LayoutDashboard,
  "ti-message":         MessageSquare,
  "ti-briefcase":       Briefcase,
  "ti-building":        Building2,
  "ti-home":            Home,
  "ti-list-check":      ListChecks,
  "ti-checklist":       ListChecks,
  "ti-target":          Target,
  "ti-settings":        Settings,
  "ti-file-invoice":    FileText,
  "ti-clipboard-list":  ClipboardList,
  "ti-chart-bar":       BarChart3,
  "ti-coins":           Coins,
  "ti-coin":            Coins,
  "ti-trending-up":     TrendingUp,
  "ti-alert-triangle":  AlertTriangle,
  "ti-alert-circle":    CircleAlert,
  "ti-box":             Package,
  "ti-users":           Users,
  "ti-sparkles":        Sparkles,
  "ti-star":            Star,
  "ti-wifi":            Wifi,
  "ti-map-pin":         MapPin,
  "ti-key":             Key,
  "ti-clock":           Clock,
  "ti-door-enter":      DoorOpen,
  "ti-eye":             Eye,
  "ti-list-details":    ListDetails,
  "ti-book":            Book,
  "ti-checkbox":        CheckSquare,
  "ti-files":           Files,
  "ti-device-desktop":  Monitor,
  "ti-lock":            Lock,
  "ti-info-circle":     Info,
  "ti-flame":           Flame,
  "ti-pin":             Pin,
  "ti-refresh":         RefreshCw,
  "ti-currency-euro":   Euro,
  "ti-world":           Globe,
  "ti-link":            Link2,
  "ti-settings-2":      Settings2,
  "ti-user":            User,
}

export default function Icon({ name, size = 18, strokeWidth = 1.8, style, className, ...rest }) {
  const Cmp = TI[name]
  if (!Cmp) return null
  return <Cmp size={size} strokeWidth={strokeWidth} style={style} className={className} aria-hidden {...rest} />
}

// Nav (catégories + sous-onglets + espaces agent/proprio) : id → ti-name.
// Source unique pour le rhabillage de la nav partagée (socle DA).
export const NAV_ICON = {
  // Admin — catégories
  calendrier: "ti-calendar",      pilotage: "ti-layout-dashboard", communication: "ti-message",
  operations: "ti-briefcase",     logements: "ti-building",        "mes-taches": "ti-list-check",
  params: "ti-settings",
  // `prospection` retiré (lot 4 opérateur B) : l'onglet n'existe plus, la file
  // vit dans Logements › À démarcher.
  // Admin — sous-onglets
  donnees: "ti-home",             facturation: "ti-file-invoice",  historique: "ti-clipboard-list",
  annuel: "ti-chart-bar",         compta: "ti-coins",              "stats-admin": "ti-trending-up",
  messages: "ti-message",         incidents: "ti-alert-triangle",  stock: "ti-box",
  // Agent / Orchestrateur
  "menage-today": "ti-sparkles",  "menage-suivi": "ti-users",      "menage-calendrier": "ti-calendar-month",
  "orchestrateur-horaires": "ti-clock",
  "menage-avis": "ti-star",
  // Nav orchestrateur 7→4 (2026-08-19) — les groupes.
  planning: "ti-calendar", "a-decider": "ti-list-check",
}
